import React, { useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayIcon, RotateCcwIcon } from 'lucide-react';
import { CountdownRing } from './components/CountdownRing';
import { StepNav } from './components/StepNav';
import { PollOverlay } from './components/PollOverlay';
import { PlaybackProvider } from './hooks/usePlayback';
import { SnakSessionProvider } from './contexts/SnakSession';
import { ScreenOpening } from './components/screens/ScreenOpening';
import { ScreenJourney } from './components/screens/ScreenJourney';
import { ScreenVariability } from './components/screens/ScreenVariability';
import { ScreenMissingLink } from './components/screens/ScreenMissingLink';
import { ScreenPayoff } from './components/screens/ScreenPayoff';
import { ScreenBonus } from './components/screens/ScreenBonus';
import { BRAND, BRAND_LOGO, TOTAL_SECONDS } from './data/teaser';
type Screen = 'opening' | 'journey' | 'variability' | 'link' | 'payoff' | 'bonus';
const CHAPTERS: Screen[] = ['journey', 'variability', 'link', 'payoff', 'bonus'];
const LABELS: Record<Screen, string> = {
  opening: 'The contract',
  journey: 'The digital journey',
  variability: 'The missing link',
  link: 'Precision Grip',
  payoff: 'The payoff',
  bonus: 'The bonus'
};
const TICK_MS = 100;
export function App() {
  const [runId, setRunId] = useState(0);
  return <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-200 px-4 py-8" data-id="element-0" data-mp="82jt1oitsae">
      <div className="relative flex h-[844px] w-[390px] flex-col overflow-hidden rounded-[3rem] border-[10px] border-gray-800 bg-solventum-bg shadow-2xl" data-id="element-1" data-mp="2zng0ch1bierc">
        <div className="absolute inset-x-0 top-0 z-30 flex h-7 justify-center bg-solventum-bg" data-id="element-2" data-mp="38l04bxqkep5n">
          <div className="h-5 w-28 rounded-b-2xl bg-gray-800" data-id="element-3" data-mp="3dlwa6guh8ldp" />
        </div>
        <div className="absolute inset-x-0 bottom-1.5 z-30 flex justify-center" data-id="element-4" data-mp="nw2bq4xkjoqk">
          <div className="h-1 w-32 rounded-full bg-white/20" data-id="element-5" data-mp="sunf2d84aoj8" />
        </div>
        <div className="mt-6 flex-1 overflow-hidden" data-id="element-6" data-mp="swqbpg3rzfwx">
          <SnakSessionProvider key={runId} data-id="element-7" data-mp="35glr3hkw7k1">
            <Snak onReplay={() => setRunId(prev => prev + 1)} data-id="element-8" data-mp="38ktuby5znfz" />
          </SnakSessionProvider>
        </div>
      </div>
    </div>;
}
function Snak({
  onReplay
}: {
  onReplay: () => void;
}) {
  const [screen, setScreen] = useState<Screen>('opening');
  const [remaining, setRemaining] = useState<number>(TOTAL_SECONDS);
  const [paused, setPaused] = useState(false);
  const [running, setRunning] = useState(true);
  const [pollOpen, setPollOpen] = useState(false);
  const [spent, setSpent] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // The clock is held while the optional aside is open — the poll costs no time
  const frozen = paused || pollOpen;
  useEffect(() => {
    if (!running || frozen) return;
    intervalRef.current = setInterval(() => {
      setRemaining(prev => Math.max(0, prev - TICK_MS / 1000));
    }, TICK_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, frozen]);
  useEffect(() => {
    if (running && remaining <= 0) setRunning(false);
  }, [running, remaining]);

  // Real attention spent on the story, whether or not the clock still had time
  useEffect(() => {
    if (frozen || screen === 'opening' || screen === 'bonus') return;
    const interval = setInterval(() => {
      setSpent(prev => prev + TICK_MS / 1000);
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [frozen, screen]);
  const goTo = (next: Screen) => {
    if (next === 'bonus') {
      setRunning(false);
      setRemaining(0);
    } else if (remaining > 0) {
      setRunning(true);
    }
    setScreen(next);
  };
  const showChrome = screen !== 'opening';
  const showPauseButton = screen !== 'bonus';
  const chapterIndex = CHAPTERS.indexOf(screen);
  const nav = (() => {
    switch (screen) {
      case 'journey':
        return <StepNav nextLabel="Continue" onNext={() => goTo('variability')} data-id="element-9" data-mp="aq9c6pjkelbx" />;
      case 'variability':
        return <StepNav backLabel="Back" onBack={() => goTo('journey')} nextLabel="Continue" onNext={() => goTo('link')} data-id="element-10" data-mp="qxxdverquw3e" />;
      case 'link':
        return <StepNav backLabel="Back" onBack={() => goTo('variability')} nextLabel="Continue" onNext={() => goTo('payoff')} data-id="element-11" data-mp="h7lvin7bckcz" />;
      case 'payoff':
        return <StepNav backLabel="Back" onBack={() => goTo('link')} nextLabel="One more thing" onNext={() => goTo('bonus')} data-id="element-12" data-mp="14wwzedjymuvc" />;
      case 'bonus':
        return <StepNav backLabel="Back" onBack={() => goTo('payoff')} data-id="element-13" data-mp="vwrss5mlc0i8" />;
      default:
        return null;
    }
  })();
  return <div className="flex h-full w-full flex-col bg-solventum-bg px-6 pb-3 pt-2 selection:bg-solventum-primary selection:text-solventum-bg" data-id="element-14" data-mp="3kp41sq7ftjba">
      {showChrome && <header className="flex w-full flex-shrink-0 items-center justify-between pb-2 pt-3" data-id="element-15" data-mp="1w4otab2j1isr">
          <div className="flex flex-col" data-id="element-16" data-mp="2060du26jndu3">
            <img src={BRAND_LOGO} alt={BRAND} className="h-6 w-auto object-contain" data-id="element-17" data-mp="206tkl35i63c8" />
            <span className="mt-1 font-body text-[10px] tracking-wide text-white/40" data-id="element-18" data-mp="253a93ygm6jpu">
              {LABELS[screen]}
            </span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-19" data-mp="2etv65hgn3g6n">
            <button onClick={onReplay} aria-label="Replay from the start" title="Replay" className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors duration-150 ease-out hover:border-solventum-primary hover:text-solventum-primary" data-id="element-20" data-mp="2jtzm96dmy8wc">
              <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-21" data-mp="tj4u1u941f39" />
            </button>
            {showPauseButton && <button onClick={() => setPaused(prev => !prev)} aria-pressed={paused} aria-label={paused ? 'Resume' : 'Pause'} className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors duration-150 ease-out hover:border-solventum-primary hover:text-solventum-primary" data-id="element-22" data-mp="ygwuxdrqgbjl">
                {paused ? <PlayIcon className="h-3.5 w-3.5" data-id="element-23" data-mp="2kpwqjef5fm6t" /> : <PauseIcon className="h-3.5 w-3.5" data-id="element-24" data-mp="2ko3j3wwpoxxr" />}
              </button>}
            <CountdownRing remaining={remaining} stopped={screen === 'bonus'} paused={frozen} data-id="element-25" data-mp="2tiqj2djlwi2m" />
          </div>
        </header>}

      <main className="relative min-h-0 flex-1 overflow-hidden" data-id="element-26" data-mp="2pjiumi78yhr0">
        <PlaybackProvider paused={frozen} data-id="element-27" data-mp="33hfrlfpm4q67">
          <div key={screen} className="h-full w-full animate-push-in" data-id="element-28" data-mp="2yhuvre5qoxxp">
            {screen === 'opening' && <ScreenOpening remaining={remaining} paused={paused} onContinue={() => goTo('journey')} data-id="element-29" data-mp="3i33eh8n7a8sr" />}
            {screen === 'journey' && <ScreenJourney data-id="element-30" data-mp="385ypz25vu18s" />}
            {screen === 'variability' && <ScreenVariability onOpenPoll={() => setPollOpen(true)} data-id="element-31" data-mp="3d4jrd4wveqyt" />}
            {screen === 'link' && <ScreenMissingLink data-id="element-32" data-mp="2zdeyqmrx6yxz" />}
            {screen === 'payoff' && <ScreenPayoff data-id="element-33" data-mp="2zbk9iw9m8ko3" />}
            {screen === 'bonus' && <ScreenBonus secondsSpent={Math.round(spent)} data-id="element-34" data-mp="3443sk8r2o80a" />}
          </div>

          <PollOverlay open={pollOpen} onClose={() => setPollOpen(false)} data-id="element-35" data-mp="2pfqqqv5azuxv" />
        </PlaybackProvider>
      </main>

      {nav}

      {chapterIndex >= 0 && <div className="flex w-full flex-shrink-0 items-center justify-center gap-1.5 pt-3" aria-hidden="true" data-id="element-36" data-mp="2ubplr1l50b79">
          {CHAPTERS.map((chapter, index) => <span key={chapter} className={`h-1 rounded-full transition-all duration-300 ease-out ${index === chapterIndex ? 'w-4 bg-solventum-primary' : 'w-1 bg-white/20'}`} data-id="element-37" data-mp="2khyz828kmau7" data-mpi={index} />)}
        </div>}

      <footer className="w-full flex-shrink-0 pt-2 text-center" data-id="element-38" data-mp="2ly4pl6yu5f8n">
        <p className="font-body text-[10px] tracking-wide text-white/25" data-id="element-39" data-mp="2lxl97uou2ywq">
          © Solventum 2026
        </p>
      </footer>
    </div>;
}